/**
 * ProviderInvitationPanel — ops match list + invite send (W15-E11).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { MailOutline } from "@mui/icons-material";
import {
  getOpportunityByOrderId,
  getMatches,
  sendInvitations,
} from "../services/providerOpportunityService.js";
import { logError } from "@/shared/utils/logger.js";

const INVITABLE_STATUSES = new Set(["open", "selection_pending"]);

const partnerLabel = (match) =>
  match?.partnerName || match?.name || match?.partnerId || "Provider";

const invitationStatusColor = (statusKey) => {
  switch (statusKey) {
    case "interested":
      return "success";
    case "declined":
      return "default";
    case "pass":
      return "warning";
    case "opened":
      return "warning";
    case "invited":
      return "info";
    case "pending_send":
      return "default";
    default:
      return "default";
  }
};

const formatInvitationResultMessage = (result) => {
  const count = result?.count ?? result?.invitations?.length ?? 0;
  const skipped = Array.isArray(result?.skipped) ? result.skipped.length : 0;

  if (count === 0 && skipped > 0) {
    return skipped === 1
      ? "No new invitations sent (1 provider already invited or ineligible)."
      : `No new invitations sent (${skipped} providers already invited or ineligible).`;
  }
  if (count === 0) {
    return "No invitations were sent.";
  }
  if (skipped > 0) {
    return count === 1
      ? `Invitation sent to 1 provider (${skipped} skipped).`
      : `Invitations sent to ${count} providers (${skipped} skipped).`;
  }
  return count === 1
    ? "Invitation sent to 1 provider."
    : `Invitations sent to ${count} providers.`;
};

/**
 * @param {object} props
 * @param {string} props.orderId
 * @param {() => void} [props.onInvited]
 */
const ProviderInvitationPanel = ({ orderId, onInvited }) => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [opportunity, setOpportunity] = useState(null);
  const [matches, setMatches] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const load = useCallback(async () => {
    if (!orderId) {
      setLoading(false);
      setOpportunity(null);
      setMatches([]);
      setSelectedIds([]);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const opp = await getOpportunityByOrderId(orderId);
      setOpportunity(opp);
      if (!opp || !INVITABLE_STATUSES.has(opp.status)) {
        setMatches([]);
        setSelectedIds([]);
        return;
      }
      const rows = await getMatches(opp.id);
      setMatches(rows);
      setSelectedIds([]);
    } catch (err) {
      logError("ProviderInvitationPanel load failed", {
        orderId,
        error: err?.message,
      });
      setError(err?.message || "Failed to load provider matches");
      setOpportunity(null);
      setMatches([]);
      setSelectedIds([]);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const eligibleIds = useMemo(
    () =>
      matches
        .filter((m) => m?.eligibility?.eligible !== false)
        .map((m) => m.partnerId)
        .filter(Boolean),
    [matches]
  );

  const allEligibleSelected =
    eligibleIds.length > 0 &&
    eligibleIds.every((id) => selectedIds.includes(id));

  const someEligibleSelected =
    eligibleIds.some((id) => selectedIds.includes(id)) && !allEligibleSelected;

  const handleToggleAll = () => {
    if (allEligibleSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds([...eligibleIds]);
    }
  };

  const handleToggleOne = (partnerId, eligible) => {
    if (!partnerId || !eligible) return;
    setSelectedIds((prev) =>
      prev.includes(partnerId)
        ? prev.filter((id) => id !== partnerId)
        : [...prev, partnerId]
    );
  };

  const handleSendSelected = async () => {
    if (!opportunity?.id || selectedIds.length === 0) return;
    setSending(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const result = await sendInvitations(opportunity.id, {
        partnerIds: selectedIds,
      });
      setSelectedIds([]);
      await load();
      setSuccessMessage(formatInvitationResultMessage(result));
      if (typeof onInvited === "function") {
        onInvited();
      }
    } catch (err) {
      setError(err?.message || "Failed to send invitations");
    } finally {
      setSending(false);
    }
  };

  const handleInviteAllEligible = async () => {
    if (!opportunity?.id || eligibleIds.length === 0) return;
    setSending(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const result = await sendInvitations(opportunity.id, {
        inviteAllEligible: true,
      });
      setSelectedIds([]);
      await load();
      setSuccessMessage(formatInvitationResultMessage(result));
      if (typeof onInvited === "function") {
        onInvited();
      }
    } catch (err) {
      setError(err?.message || "Failed to send invitations");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3 }} data-testid="provider-invitation-panel-loading">
        <CardContent>
          <Box display="flex" alignItems="center" gap={1.5}>
            <CircularProgress size={22} />
            <Typography variant="body2" color="text.secondary">
              Loading provider matches…
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!opportunity || !INVITABLE_STATUSES.has(opportunity.status)) {
    return null;
  }

  return (
    <Card sx={{ mb: 3 }} data-testid="provider-invitation-panel">
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
          mb={2}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <MailOutline color="primary" />
            <Box>
              <Typography variant="h6">Provider invitations</Typography>
              <Typography variant="body2" color="text.secondary">
                {opportunity.reference || opportunity.id} · Match and invite
                providers
              </Typography>
            </Box>
          </Stack>
          <Chip
            size="small"
            label={`${matches.length} match${matches.length === 1 ? "" : "es"}`}
            color="primary"
            variant="outlined"
          />
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {successMessage ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        ) : null}

        <Divider sx={{ mb: 2 }} />

        {matches.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No matched providers found for this opportunity.
          </Typography>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-label="Provider matches">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={someEligibleSelected}
                        checked={allEligibleSelected}
                        onChange={handleToggleAll}
                        disabled={eligibleIds.length === 0 || sending}
                        inputProps={{ "aria-label": "Select all eligible" }}
                      />
                    </TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Score</TableCell>
                    <TableCell>Match reasons</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Eligibility</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matches.map((match) => {
                    const eligible = match?.eligibility?.eligible !== false;
                    const partnerId = match.partnerId;
                    const checked = selectedIds.includes(partnerId);
                    return (
                      <TableRow key={partnerId} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={checked}
                            onChange={() => handleToggleOne(partnerId, eligible)}
                            disabled={!eligible || sending}
                            inputProps={{
                              "aria-label": `Select ${partnerLabel(match)}`,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {partnerLabel(match)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {partnerId}
                          </Typography>
                        </TableCell>
                        <TableCell>{match.score ?? "—"}</TableCell>
                        <TableCell>
                          {(match.matchReasons || []).slice(0, 3).join(", ") ||
                            "—"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={match.invitationStatusLabel || "Not invited"}
                            color={invitationStatusColor(match.invitationStatus)}
                            variant={
                              match.invitationStatus === "not_invited"
                                ? "outlined"
                                : "filled"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {eligible ? (
                            <Chip
                              size="small"
                              label="Eligible"
                              color="success"
                            />
                          ) : (
                            <Chip
                              size="small"
                              label={
                                (match?.eligibility?.reasons || [])
                                  .slice(0, 2)
                                  .join(", ") || "Ineligible"
                              }
                              color="default"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent="flex-end"
              sx={{ mt: 2 }}
            >
              <Button
                variant="outlined"
                disabled={eligibleIds.length === 0 || sending}
                onClick={handleInviteAllEligible}
                sx={{ textTransform: "none" }}
              >
                Invite all eligible
              </Button>
              <Button
                variant="contained"
                disabled={selectedIds.length === 0 || sending}
                onClick={handleSendSelected}
                sx={{ textTransform: "none" }}
              >
                {sending ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  `Send invitations${selectedIds.length ? ` (${selectedIds.length})` : ""}`
                )}
              </Button>
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ProviderInvitationPanel;
